/**
 * FeeMedidor — consumo del fee en vivo (brand book SOA 10 PRO §7).
 * Número grande + «de N créditos usados», barra segmentada por tipo y estado de ritmo en píldora
 * (Sobrado · Ritmo justo · Te pasás). Si el cliente no tiene tope, lo dice en vez de inventar un número.
 * `compacto` = una línea para CrearTareaModal («Usa 1 crédito · quedan 9»).
 */
import { useEffect, useState } from "react";
import { api } from "../api/client.js";

const COLORES = ["#8B5CF6", "#22D3EE", "#10B981", "#F59E0B", "#EC4899", "#6366F1"];
const RITMO = {
  sobrado:  { label: "Sobrado",     bg: "#D1FAE5", fg: "#065F46" },
  justo:    { label: "Ritmo justo", bg: "#FEF3C7", fg: "#92400E" },
  te_pasas: { label: "Te pasás",    bg: "#FEE2E2", fg: "#991B1B" },
};

export function useFeeVivo(clienteId, mes) {
  const [fee, setFee] = useState(null);
  useEffect(() => {
    if (!clienteId) { setFee(null); return; }
    let vivo = true;
    api.getFeeVivo(clienteId, mes).then(r => { if (vivo) setFee(r); }).catch(() => { if (vivo) setFee(null); });
    return () => { vivo = false; };
  }, [clienteId, mes]);
  return fee;
}

export function FeeLinea({ clienteId, creditos = 1 }) {
  const fee = useFeeVivo(clienteId);
  if (!fee || fee.ilimitado) return null;
  const quedan = Math.max(0, (fee.restantes ?? 0) - creditos);
  const excede = (fee.restantes ?? 0) < creditos;
  return (
    <div role="status" className="text-[13px] font-semibold" style={{ color: excede ? "#991B1B" : "inherit" }}>
      {excede
        ? `Usa ${creditos} ${creditos === 1 ? "crédito" : "créditos"} y se pasa del plan: se registra como excedente y se avisa al EC.`
        : `Usa ${creditos} ${creditos === 1 ? "crédito" : "créditos"} · quedan ${quedan}`}
    </div>
  );
}

export default function FeeMedidor({ clienteId, mes }) {
  const fee = useFeeVivo(clienteId, mes);
  if (!fee) return null;
  if (fee.ilimitado) {
    return (
      <section aria-label="Consumo del fee" className="rounded-[24px] bg-card p-6">
        <div className="text-[13px] font-bold uppercase tracking-wider text-muted-foreground">Consumo del fee · {fee.periodo}</div>
        <div className="mt-2 text-[22px] font-extrabold">Sin tope de créditos este mes</div>
        <p className="mt-1 text-[14px] text-muted-foreground">Se llevaron {fee.usados} {fee.usados === 1 ? "pieza" : "piezas"} hasta hoy. Cargá los créditos del contrato en el Fee Ledger para ver el ritmo.</p>
      </section>
    );
  }
  const r = RITMO[fee.ritmo];
  return (
    <section aria-label="Consumo del fee" className="rounded-[24px] bg-card p-6">
      <div className="flex items-center justify-between gap-3">
        <div className="text-[13px] font-bold uppercase tracking-wider text-muted-foreground">Consumo del fee · {fee.periodo}</div>
        {r && <span className="rounded-full px-3 py-1 text-[12px] font-extrabold" style={{ background: r.bg, color: r.fg }}>{r.label}</span>}
      </div>
      <div className="mt-3 flex items-baseline gap-3">
        <span className="text-[56px] leading-none font-black tracking-tight tabular-nums">{fee.usados}</span>
        <span className="text-[16px] text-muted-foreground">de {fee.total} créditos usados</span>
      </div>
      <div className="mt-4 flex h-4 w-full overflow-hidden rounded-lg bg-[#F2F3F6]" role="img" aria-label={`${fee.usados} de ${fee.total} créditos`}>
        {fee.por_tipo.map((t, i) => (
          <div key={t.id} title={`${t.label}: ${t.usados}`} style={{ width: `${Math.min(100, (t.usados / fee.total) * 100)}%`, background: COLORES[i % COLORES.length] }} />
        ))}
      </div>
      <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-[13px] text-muted-foreground">
        {fee.por_tipo.map((t, i) => (
          <li key={t.id} className="inline-flex items-center gap-1.5">
            <span aria-hidden="true" className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: COLORES[i % COLORES.length] }} />
            {t.label} {t.usados}/{t.total}
          </li>
        ))}
      </ul>
      <p className="mt-3 text-[13px] text-muted-foreground">Al ritmo actual terminás el mes con {fee.proyeccion_fin_mes} de {fee.total}.</p>
    </section>
  );
}
